import { useContext, useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip, Zoom } from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import moment from "moment";
import { Button, COLORS, Icon, Modal, Tab } from "abs-ui-components";
import Verification from "./Verification";
import axiosHttp from "../../../services/interceptor";
import { debounce } from "lodash";
import { ClaimsContext } from "../../../services/ClaimsService";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import BreadCrumb from "../../../common/breadCrumb/BreadCrumb";
import { FeatureType } from "../../../constants/FeatureToggles";
import "./FEUComplianceBalanceReportsPage.scss";

const FEUComplianceBalanceReportDetailsPage = () => {
    const baseURL = process.env.REACT_APP_BASE_URL;

    const params = useParams();
    const reportMstId = params.id;
    const [reportDetails, setReportDetails] = useState(null);
    const [selectedTab, setActiveTab] = useState(0);
    const [refreshKey, setRefreshKey] = useState(0);
    const [showbreadCrumb, setShowBreadCrumb] = useState(() => {
        const storedValue = sessionStorage.getItem("header-footer");
        return storedValue !== null ? JSON.parse(storedValue) : true;
    });

    const { userHasFeature, userFeaturesAreLoaded } = useContext(ClaimsContext);
    const navigate = useNavigate();

    dayjs.extend(relativeTime);

    // Create a debounced setter function
    const debouncedSetHeaderFooterEnabled = debounce((value) => {
        setShowBreadCrumb(value);
    }, 100);
    var feuCbvrEnabled = (userFeaturesAreLoaded && userHasFeature(FeatureType.ShowFeuCbvr));

    useEffect(() => {
        const storedValue = sessionStorage.getItem("header-footer");
        if (storedValue !== null) {
            debouncedSetHeaderFooterEnabled(JSON.parse(storedValue));
        }
    }, [debouncedSetHeaderFooterEnabled]);

    const breadcrumb = useMemo(
        () => [
            {
                label: "Annual Emission Verification",
                url: "",
                navId: "annual_emission_verification_navid",
            },
            {
                label: "FEU Compliance Balance Reports",
                url: "/feu-compliance-balance-reports",
                navId: "feu_compliance_balance_reports_navid",
            },
            {
                label: "Verification",
                url: "",
                navId: "verification_navid",
            }
        ],
        []
    );

    const getReportDetailsByreportId = async (reportMasterId) => {
        await axiosHttp.get(`${baseURL}api/FeuComplianceBalance/verification-details?reportId=${reportMasterId}`).then((response) => {
            if (response) {
                setReportDetails(response?.data?.data);
            }
        });
    };

    useEffect(() => {
        if (reportMstId) {
            getReportDetailsByreportId(reportMstId);
        }
    }, [refreshKey, reportMstId]);

    const icon = (() => {
        switch (reportDetails?.status) {
            case "Verification Completed":
                return "check";
            case "No Data Submitted":
                return "info";
            case "Verification In Progress":
                return (
                    <HourglassEmptyIcon
                        sx={{ fontSize: 18, marginTop: "-6px" }}
                    />
                );
            case "Crosscheck In Progress":
                return (
                    <HourglassEmptyIcon
                        sx={{ fontSize: 18, marginTop: "-6px" }}
                    />
                );
            case "Supporting Documents Needed":
                return (
                    <InsertDriveFileIcon
                        sx={{ fontSize: 15, marginTop: "-6px" }}
                    />
                );
            default:
                return null;
        }
    })();

    const formatDate = (dateString) => {
        return moment(dateString).format("DD-MMM-YYYY");
    };

    const formattedDate = reportDetails?.dateOfIssuance
        ? formatDate(reportDetails?.dateOfIssuance)
        : "";

    const tooltipTitle =
        reportDetails?.status === "Verification Completed" ? (
            <>
                Reviewer : <span className="font-style">{reportDetails?.latestReviewer}</span>
                <br />
                Verifier : <span className="font-style">{reportDetails?.latestVerifier}</span>
                <br />
                Verification Complete Date : {formattedDate}
            </>
        ) : (
            ""
        );

    const handleOk = () => {
        navigate("/feu-compliance-balance-reports");
    };

    const handleRefresh = () => {
        setRefreshKey((prevKey) => prevKey + 1);
    };

    return (
        <>
            {feuCbvrEnabled ? (
                <div className="m-4 details-container">
                    {showbreadCrumb && <BreadCrumb items={breadcrumb} />}
                    <div className="mb-2 d-flex justify-content-between">
                        <div className="report-info">
                            <span className="header">
                                {reportDetails?.feuCbvrId}
                            </span>
                            {reportDetails?.companyImoNumber && (
                                <span className="text-gray ml-4 fw-bolder">
                                    {reportDetails?.companyName}, IMO#{" "} {reportDetails?.companyImoNumber}
                                </span>
                            )}
                        </div>

                        <div className="d-flex">
                            {reportDetails?.reportId &&
                                (
                                    <>
                                        <Button
                                            variant="tertiary"
                                            startIcon={<Icon icon="arrow_back" />}
                                            className="mr-4"
                                            onClick={() => {
                                                navigate("/feu-compliance-balance-reports");
                                            }}
                                        >
                                            Return to Compliance Balance Reports
                                        </Button>
                                    </>
                                )}

                            {reportDetails?.status && (
                                <Tooltip
                                    title={tooltipTitle}
                                    disableHoverListener={!tooltipTitle}
                                    placement="top"
                                    TransitionComponent={Zoom}
                                    followCursor={true}
                                    arrow
                                    componentsProps={{
                                        tooltip: {
                                            sx: {
                                                fontSize: "13px",
                                                backgroundColor:
                                                    "#012c51 !important",
                                                color: "white",
                                            },
                                        },
                                    }}
                                >
                                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                        <Button
                                            variant="tertiary"
                                            startIcon={
                                                icon && <Icon icon={icon} />
                                            }
                                            className="mr-4"
                                            disabled={true}
                                        >
                                            {reportDetails?.status}
                                        </Button>
                                    </span>
                                </Tooltip>
                            )}
                        </div>
                    </div>
                    <Tab
                        tabData={[{
                            header: "Verification",
                            content: (
                                <>
                                    {selectedTab === 0 && (
                                        <Verification reportDetails={reportDetails} handleRefresh={handleRefresh} />
                                    )}
                                </>
                            ),
                        }].map((tab) => ({
                            ...tab,
                            header: (
                                <span
                                    style={{
                                        textTransform: "capitalize",
                                    }}
                                >
                                    {tab.header}
                                </span>
                            ),
                        }))}
                        // onTabChange={tabChangeHandler}
                        activeTab={selectedTab}
                    ></Tab>
                </div>
            ) : (
                <div>
                    <Modal
                        isOpen={!feuCbvrEnabled}
                        modalHeader
                        padding={"70px"}
                        headerHeight={"50px"}
                        showFooter
                        footer={
                            <div className="d-flex justify-content-end fixed-action-btns p-4">
                                <Button
                                    className="modal-delete-btn"
                                    onClick={handleOk}
                                    primaryColor={COLORS.RED.DARK}
                                    primaryBackgroundColor={COLORS.RED.LIGHT}
                                >
                                    Ok
                                </Button>
                            </div>
                        }
                    >
                        <div>You are not authorized to view this.</div>
                    </Modal>
                </div>
            )}
        </>
    );
}

export default FEUComplianceBalanceReportDetailsPage;